import { SEO, breadcrumbJsonLd, SITE_URL } from "@/components/SEO";
import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { articulos, colorMap } from "@/data/articulos";
import { ClownNose, CometaBanner, CometaSwoosh, LiveBackground } from "@/components/ClownNose";
import { ArticuloCard } from "@/components/ArticuloCard";
import { Newsletter } from "@/components/Newsletter";
import { ShareBar, ReadingProgress, FloatingShare } from "@/components/ShareBar";
import { NotasRelacionadas } from "@/components/NotasRelacionadas";

export default function BlogPost() {
  const { slug } = useParams();
  const a = articulos.find((x) => x.slug === slug);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);
  if (!a) return <Navigate to="/blog" replace />;
  const c = colorMap[a.color];

  return (
    <article data-testid="blog-post">
      <ReadingProgress color="#E01F26" />
      <FloatingShare title={a.titulo} />
      <SEO title={a.titulo} description={`${a.resumen} ${a.categoria} · Revista Cometa, por Dra. Cometa (Araceli Lugo).`.slice(0, 160)} type="article" image={a.portada || "/og-image.jpg"} jsonLd={[{ "@context": "https://schema.org", "@type": "BlogPosting", headline: a.titulo, description: a.resumen, articleSection: a.categoria, image: a.portada ? `${SITE_URL}${a.portada}` : undefined, mainEntityOfPage: `${SITE_URL}${window.location.pathname}`, author: { "@type": "Person", name: "Araceli Lugo", alternateName: "Dra. Cometa", url: SITE_URL }, publisher: { "@type": "Organization", name: "Dra. Cometa", logo: { "@type": "ImageObject", url: `${SITE_URL}/icon-512.png` } }, inLanguage: "es-MX" }, breadcrumbJsonLd([{ name: "Revista Cometa", path: "/blog" }, { name: a.titulo, path: `/blog/${a.slug}` }])]} />
      <header ref={heroRef} className={`${c.bg} ${c.fg} relative overflow-hidden min-h-[70vh] flex items-end`} data-testid="blog-post-hero">
        {a.portada && <><motion.img style={{ y: yImg, scale: scaleImg }} loading="eager" src={a.portada} alt={a.titulo} className="absolute inset-0 w-full h-full object-cover grayscale opacity-45" data-testid="blog-post-portada" /><div className={`absolute inset-0 bg-gradient-to-t ${c.bg.replace("bg-", "from-")} via-cometa-tinta/30 to-transparent`} /></>}
        <CometaSwoosh className="absolute inset-0 w-full h-full" color="#FFBC01" opacity={0.25} />
        {[0, 1, 2].map((i) => <motion.span key={i} className="absolute" style={{ left: `${15 + i * 30}%`, top: `${20 + i * 15}%` }} animate={{ y: [0, -18, 0], rotate: [0, 8, 0] }} transition={{ duration: 6 + i * 2, repeat: Infinity, ease: "easeInOut" }}><ClownNose size={18 + i * 8} /></motion.span>)}
        <motion.div style={{ y: yText }} className="max-w-4xl mx-auto px-6 pt-32 pb-16 lg:pb-24 relative w-full">
          <Link to="/blog" data-testid="blog-post-back" className="inline-flex items-center gap-2 text-sm font-semibold opacity-90 hover:opacity-100 mb-10"><ArrowLeft size={16} /> Volver al blog</Link>
          <div className="flex items-center gap-3 mb-6">
            <ClownNose size={30} className="border-2 border-white/70" />
            <span className="text-xs tracking-[0.3em] font-bold uppercase">{a.categoria}</span>
          </div>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-5xl md:text-7xl lg:text-8xl leading-[0.98] mb-6">{a.titulo}</motion.h1>
          <p className="inline-flex items-center gap-2 text-sm opacity-85"><Clock size={14} /> {a.lectura} de lectura · Dra. Cometa</p>
          <ShareBar title={a.titulo} className="mt-8" />
        </motion.div>
      </header>

      <div className="relative blog-aurora overflow-hidden"><LiveBackground /><div className="dropcap relative max-w-3xl mx-auto px-6 py-16 md:py-24 text-lg lg:text-xl leading-relaxed text-cometa-tinta/85">
        {a.secciones.map((s, i) => (
          <motion.section key={i} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6 }} className="mb-10">
            {s.titulo && <h2 className={`text-3xl md:text-4xl mb-6 mt-4 flex items-start gap-3 ${c.text}`}><ClownNose size={14} className="mt-3" />{s.titulo}</h2>}
            {s.texto?.map((t, j) => <p key={j} className="mb-3">{t}</p>)}
            {s.lista && (
              <ul className="my-6 space-y-2">
                {s.lista.map((l, j) => <li key={j} className="flex items-start gap-3"><span className={`mt-2.5 w-2.5 h-2.5 rounded-full shrink-0 ${c.bg}`} />{l}</li>)}
              </ul>
            )}
            {s.cita && <motion.blockquote initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className={`relative my-10 pl-8 border-l-4 ${c.bg.replace("bg-", "border-")}`}><span className="absolute -left-[14px] -top-2"><ClownNose size={22} /></span><p className={`font-script text-4xl md:text-6xl ${c.text} leading-tight`}>{s.cita}</p></motion.blockquote>}
            {s.cta && <Link to={s.cta.to} data-testid="blog-post-cta" className="btn-gold px-8 py-4 mt-4">{s.cta.label} <ArrowRight size={18} /></Link>}
          </motion.section>
        ))}

        <div className={`${c.bg} ${c.fg} rounded-[2rem] p-8 lg:p-10 mt-16 flex flex-col md:flex-row md:items-center justify-between gap-6`} data-testid="blog-post-share-end">
          <p className="font-script text-3xl md:text-4xl leading-tight">¿Te movió algo? Compártelo con alguien que lo necesite.</p>
          <ShareBar title={a.titulo} />
        </div>
        <div className={`${c.soft} rounded-[2rem] p-8 lg:p-12 mt-10`}>
          <h3 className="text-3xl mb-8">Preguntas frecuentes</h3>
          <div className="space-y-6">
            {a.faq.map((f, i) => (
              <div key={i} data-testid={`faq-${i}`}>
                <p className={`font-semibold ${c.text} mb-1`}>{f.q}</p>
                <p className="text-base lg:text-lg">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      </div>
      <div className="max-w-6xl mx-auto px-6 mb-24">
        <CometaBanner variant="strip" />
      </div>

      <NotasRelacionadas todas={articulos} actual={a} />
      <Newsletter inverse />
    </article>
  );
}
